import { useEffect, useRef, useState } from "react";

import { useLogoutOnTwoFactorError } from "@/components/Welcome/AuthenticatorSetup/authenticator.utils";
import {
  useCancelSetup,
  useChangeTwoFactorAuthentication,
  useSetUpTwoFactorAuthentication,
} from "@/services/auth";
import {
  ChangeTwoFactorAuthenticationParams,
  SetUpTwoFactorAuthenticationReponse,
} from "@/services/model";

export const useRequestTwoFactorAppSetup = (
  useMutation:
    | typeof useSetUpTwoFactorAuthentication
    | typeof useChangeTwoFactorAuthentication,
  params: ChangeTwoFactorAuthenticationParams,
  shouldLogout = true,
) => {
  const [authSetupData, setAuthSetupData] = useState<
    SetUpTwoFactorAuthenticationReponse | undefined
  >(undefined);
  const [hasFailed, setHasFailed] = useState(false);
  const hasRequestedSetup = useRef(false);

  const { requestChange, data } = useRequestWithCancel(
    useMutation,
    params,
    shouldLogout,
    () => setHasFailed(true),
  );

  useEffect(() => {
    if (hasRequestedSetup.current) {
      return;
    }
    hasRequestedSetup.current = true;
    requestChange()
      .then((data) => {
        setAuthSetupData(data);
      })
      .catch(() => {
        // Already surfaced to the caller via `onUnrecoverableError` below;
        // caught here only so it isn't also logged as an unhandled rejection.
      });
  }, [requestChange]);

  return { authSetupData: data || authSetupData, hasFailed };
};

const useRequestWithCancel = (
  useMutation:
    | typeof useSetUpTwoFactorAuthentication
    | typeof useChangeTwoFactorAuthentication,
  params: ChangeTwoFactorAuthenticationParams,
  shouldLogout = true,
  onUnrecoverableError: () => void,
) => {
  const [data, setData] = useState<SetUpTwoFactorAuthenticationReponse | null>(
    null,
  );
  const logoutOnError = useLogoutOnTwoFactorError();

  const { mutateAsync: cancelSetup } = useCancelSetup({
    mutation: {
      onSuccess: () => {
        // We need to wait before we can request a new setup
        setTimeout(() => {
          requestChange({ params })
            .then((data) => {
              setData(data);
            })
            .catch(onUnrecoverableError);
        }, 5000);
      },
      onError: () => null,
    },
  });

  const { mutateAsync: requestChange } = useMutation({
    mutation: {
      onError: (error: any) => {
        if (shouldLogout) {
          logoutOnError(error);
        }
        const isSetupAlreadyInProgress =
          (error.response?.status === 400 &&
            error.response.data?.errorCode === 403) ||
          (error.response?.status === 500 &&
            typeof error.response.data === "string" &&
            error.response.data.includes(
              "TwoFactorAuthentication change in progress",
            ));

        if (isSetupAlreadyInProgress) {
          cancelSetup().catch(onUnrecoverableError);
        } else {
          // e.g. the method is already active for this account -- there's no
          // in-progress setup to cancel and retry, so the request just fails.
          onUnrecoverableError();
        }
      },
    },
  });

  return { requestChange: () => requestChange({ params }), data };
};
